import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Toolbar,
  Checkbox,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  FilterAlt as FilterAltIcon,
  Language as LanguageIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  Star as StarIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as RestartAltIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  EmojiEvents as TrophyIcon,
  LocalLibrary as LibraryIcon,
  Download as DownloadIcon,
  Gavel as GavelIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Defensive: always return an array regardless of how data arrives from API

const toArray = (v) => {
  if (Array.isArray(v)) return v;
  if (!v) return [];
  try {
    const p = JSON.parse(v);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};

// Add missing constants if not defined elsewhere
const ZOOM_STORAGE_KEY = 'trainingProgramsZoom';
const API_BASE_URL = '/api';

// Per-card accent color palette (cycles if more programs added)
const ACCENT_COLORS = [
  { dark: '#4f46e5', mid: '#6366f1', light: '#eef2ff', text: '#4f46e5' }, // indigo
  { dark: '#0891b2', mid: '#06b6d4', light: '#ecfeff', text: '#0891b2' }, // cyan
  { dark: '#d97706', mid: '#f59e0b', light: '#fffbeb', text: '#b45309' }, // amber
  { dark: '#7c3aed', mid: '#8b5cf6', light: '#f5f3ff', text: '#7c3aed' }, // violet
  { dark: '#059669', mid: '#10b981', light: '#ecfdf5', text: '#047857' }, // emerald
  { dark: '#db2777', mid: '#ec4899', light: '#fdf2f8', text: '#be185d' }, // pink
  { dark: '#2563eb', mid: '#3b82f6', light: '#eff6ff', text: '#1d4ed8' }, // blue
  { dark: '#ea580c', mid: '#f97316', light: '#fff7ed', text: '#c2410c' }, // orange
  { dark: '#0d9488', mid: '#14b8a6', light: '#f0fdfa', text: '#0f766e' }, // teal
  { dark: '#9333ea', mid: '#a855f7', light: '#faf5ff', text: '#7e22ce' }, // purple
];

const LINE_COLORS = [
  { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8' },
  { bg: '#ecfeff', border: '#67e8f9', text: '#0e7490' },
  { bg: '#f0fdf4', border: '#86efac', text: '#15803d' },
  { bg: '#fefce8', border: '#fde68a', text: '#a16207' },
  { bg: '#fff7ed', border: '#fdba74', text: '#c2410c' },
  { bg: '#fdf2f8', border: '#f9a8d4', text: '#be185d' },
  { bg: '#eef2ff', border: '#a5b4fc', text: '#4338ca' },
  { bg: '#f5f3ff', border: '#c4b5fd', text: '#6d28d9' },
];

const FALLBACK_CAMPUS_IMAGES = [
  { src: '/assets/images/CAMPUS/classroom.jpg', title: 'Classroom Session' },
  { src: '/assets/images/CAMPUS/LAB.jpeg', title: 'Main Lab' },
  { src: '/assets/images/CAMPUS/LAB2.jpg', title: 'System Lab' },
  { src: '/assets/images/CAMPUS/LAB3.jpeg', title: 'Practical Lab' },
];


const TrainingPrograms = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  // APK Download Modal State
  const [apkDialogOpen, setApkDialogOpen] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  // Google Drive APK Link (provided by user)
  const APK_LINK = 'https://drive.google.com/uc?export=download&id=1oPD_XVNVqP8LkdLR5OyfXTqBGEN4_JnA';

  const [programs, setPrograms] = useState([]);
  const [filters, setFilters] = useState({ categories: [], modes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [mode, setMode] = useState('');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sort, setSort] = useState('display');
  const [view, setView] = useState('grid');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedAccent, setSelectedAccent] = useState(ACCENT_COLORS[0]);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [highlightPreview, setHighlightPreview] = useState(null);
  const [activeHighlightIndex, setActiveHighlightIndex] = useState(null);
  const [activeSyllabusIndex, setActiveSyllabusIndex] = useState(null);
  const [campusImages, setCampusImages] = useState(FALLBACK_CAMPUS_IMAGES);
  const [campusPreviewOpen, setCampusPreviewOpen] = useState(false);
  const [activeCampusImageIndex, setActiveCampusImageIndex] = useState(0);

  const selectedHighlights = useMemo(
    () => (selectedProgram ? toArray(selectedProgram.highlights) : []),
    [selectedProgram]
  );

  const selectedSyllabus = useMemo(
    () => (selectedProgram ? toArray(selectedProgram.syllabus) : []),
    [selectedProgram]
  );

  useEffect(() => {
    document.title = 'KNLS - Training Programs';
    const storedZoom = Number(window.localStorage.getItem(ZOOM_STORAGE_KEY) || 100);
    if (!Number.isNaN(storedZoom)) {
      setZoomPercent(Math.min(130, Math.max(85, storedZoom)));
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(ZOOM_STORAGE_KEY, String(zoomPercent));
  }, [zoomPercent]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/training-programs`, {
        params: { search, category, mode, featured: featuredOnly ? 'true' : '', sort },
      });
      if (response.data?.success) {
        setPrograms(Array.isArray(response.data.programs) ? response.data.programs : []);
        setFilters(response.data.filters || { categories: [], modes: [] });
      } else {
        setPrograms([]);
      }
    } catch (err) {
      console.error('Failed to load training programs:', err);
      setError('Could not load training programs from database.');
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampusImages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/training-programs/campus-images`);
      const apiImages = Array.isArray(response?.data?.images)
        ? response.data.images
            .filter((item) => item?.image_url)
            .map((item) => ({
              src: item.image_url,
              title: item.image_title || 'Campus Photo',
            }))
        : [];

      setCampusImages(apiImages.length > 0 ? apiImages : FALLBACK_CAMPUS_IMAGES);
    } catch (err) {
      console.error('Failed to load campus images:', err);
      setCampusImages(FALLBACK_CAMPUS_IMAGES);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchPrograms, 250);
    return () => clearTimeout(timer);
  }, [search, category, mode, featuredOnly, sort]);

  useEffect(() => {
    fetchCampusImages();
  }, []);

  useEffect(() => {
    if (!selectedProgram) {
      setHighlightPreview(null);
      setActiveHighlightIndex(null);
      setActiveSyllabusIndex(null);
    }
  }, [selectedProgram]);

  useEffect(() => {
    if (!highlightPreview || highlightPreview.visible) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setHighlightPreview(null);
    }, 220);

    return () => window.clearTimeout(timer);
  }, [highlightPreview]);

  const stats = useMemo(() => {
    const total = programs.length;
    const featuredCount = programs.filter((item) => item.is_featured).length;
    return { total, featuredCount };
  }, [programs]);

  const zoomIn = () => setZoomPercent((prev) => Math.min(130, prev + 5));
  const zoomOut = () => setZoomPercent((prev) => Math.max(85, prev - 5));
  const resetZoom = () => setZoomPercent(100);

  const openProgram = (program, accent) => {
    setSelectedProgram(program);
    setSelectedAccent(accent);
  };

  const showHighlightPreview = (item, index) => {
    setHighlightPreview({ item, index, source: 'highlights', visible: true });
  };

  const showSyllabusPreview = (item, index) => {
    setHighlightPreview({ item, index, source: 'syllabus', visible: true });
  };

  const hideHighlightPreview = () => {
    setHighlightPreview((current) => (current ? { ...current, visible: false } : current));
  };

  const navigateHighlight = (direction) => {
    if (!highlightPreview) {
      return;
    }

    const activeItems = highlightPreview.source === 'syllabus' ? selectedSyllabus : selectedHighlights;
    if (!activeItems.length) {
      return;
    }

    setHighlightPreview((current) => {
      const currentIndex = Number.isInteger(current?.index) ? current.index : 0;
      const nextIndex = (currentIndex + direction + activeItems.length) % activeItems.length;
      return {
        item: activeItems[nextIndex],
        index: nextIndex,
        source: current?.source || 'highlights',
        visible: true,
      };
    });
  };

  const openCampusPreview = (index) => {
    setActiveCampusImageIndex(index);
    setCampusPreviewOpen(true);
  };

  const closeCampusPreview = () => {
    setCampusPreviewOpen(false);
  };

  const moveCampusImage = (direction) => {
    setActiveCampusImageIndex((current) =>
      (current + direction + campusImages.length) % campusImages.length
    );
  };

  useEffect(() => {
    setActiveCampusImageIndex((current) => {
      if (!campusImages.length) return 0;
      return Math.min(current, campusImages.length - 1);
    });
  }, [campusImages]);

  useEffect(() => {
    if (!highlightPreview) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        hideHighlightPreview();
        return;
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        navigateHighlight(1);
        return;
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        navigateHighlight(-1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [highlightPreview, selectedHighlights, selectedSyllabus]);

  useEffect(() => {
    if (!campusPreviewOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCampusPreview();
        return;
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        moveCampusImage(1);
        return;
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveCampusImage(-1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [campusPreviewOpen]);

  const ProgramCard = ({ program, accent }) => {
    const syllabus = toArray(program.syllabus);

    return (
      <Card
        elevation={0}
        sx={{
          height: '100%',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(15,23,42,0.07)',
          transition: 'transform 0.28s ease, box-shadow 0.28s ease',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: `0 20px 40px ${accent.dark}33`,
          },
          // animate title label on hover
          '&:hover .card-title': {
            color: accent.text,
            transform: 'scale(1.035)',
          },
          '&:hover .card-price': {
            color: accent.dark,
            transform: 'scale(1.06)',
          },
          '&:hover .view-btn': {
            background: `linear-gradient(135deg, ${accent.dark} 0%, ${accent.mid} 100%)`,
          },
        }}
      >
        {/* Colored top banner */}
        <Box
          sx={{
            height: 6,
            background: `linear-gradient(90deg, ${accent.dark}, ${accent.mid})`,
          }}
        />

        <CardContent sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 6px)', p: 2.5 }}>
          {/* Header row */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1.2 }}>
            <Chip
              label={program.program_code}
              size="small"
              sx={{
                bgcolor: accent.light,
                color: accent.text,
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: 0.5,
              }}
            />
            {program.is_featured && (
              <Chip
                size="small"
                icon={<StarIcon sx={{ fontSize: '0.85rem !important', color: '#f59e0b !important' }} />}
                label="Featured"
                sx={{ bgcolor: '#fffbeb', color: '#b45309', fontWeight: 700, fontSize: '0.7rem' }}
              />
            )}
          </Stack>

          {/* Title – animated on hover */}
          <Typography
            className="card-title"
            variant="h6"
            fontWeight={800}
            sx={{
              mb: 0.8,
              lineHeight: 1.3,
              display: 'inline-block',
              transition: 'color 0.25s ease, transform 0.25s ease',
              transformOrigin: 'left center',
              cursor: 'default',
            }}
          >
            {program.title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 38 }}>
            {program.subtitle || program.description || ''}
          </Typography>

          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              icon={<SchoolIcon sx={{ fontSize: '0.8rem !important' }} />}
              label={program.category}
              sx={{ bgcolor: accent.light, color: accent.text, fontWeight: 600 }}
            />
            <Chip
              size="small"
              icon={<AccessTimeIcon sx={{ fontSize: '0.8rem !important' }} />}
              label={`${program.duration_days} Days`}
            />
            <Chip
              size="small"
              icon={<LanguageIcon sx={{ fontSize: '0.8rem !important' }} />}
              label={program.language || 'English'}
            />
          </Stack>

          {/* Syllabus preview – first 3 items */}
          {syllabus.length > 0 && (
            <Stack spacing={0.3} sx={{ mb: 1.5 }}>
              {syllabus.slice(0, 3).map((item, i) => (
                <Stack key={i} direction="row" spacing={0.6} alignItems="center">
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: accent.mid,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {item}
                  </Typography>
                </Stack>
              ))}
              {syllabus.length > 3 && (
                <Typography variant="caption" sx={{ color: accent.text, fontWeight: 600, pl: 1.5 }}>
                  +{syllabus.length - 3} more topics...
                </Typography>
              )}
            </Stack>
          )}

          <Box sx={{ mt: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              {/* Price – animated on hover */}
              <Typography
                className="card-price"
                variant="h5"
                fontWeight={900}
                sx={{
                  color: accent.text,
                  transition: 'color 0.25s ease, transform 0.25s ease',
                  transformOrigin: 'left center',
                  display: 'inline-block',
                  cursor: 'default',
                }}
              >
                ₹ {Number(program.fee || 0).toLocaleString('en-IN')}
              </Typography>
              <Chip
                label={program.delivery_mode}
                size="small"
                sx={{ bgcolor: accent.light, color: accent.text, fontWeight: 600 }}
              />
            </Stack>

            <Button
              className="view-btn"
              fullWidth
              variant="contained"
              onClick={() => openProgram(program, accent)}
              sx={{
                background: `linear-gradient(135deg, ${accent.dark}dd 0%, ${accent.mid} 100%)`,
                borderRadius: 2,
                fontWeight: 700,
                letterSpacing: 0.5,
                transition: 'background 0.3s ease, transform 0.2s ease',
                '&:hover': { transform: 'scale(1.02)' },
              }}
            >
              View Program Details
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      {/* Menu Bar */}
      <AppBar position="sticky" color="default" elevation={2} sx={{ mb: 2, background: 'linear-gradient(90deg, #1e1b4b 0%, #4f46e5 100%)', color: 'white' }}>
        <Toolbar sx={{ flexWrap: 'wrap', gap: 1, py: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 900, letterSpacing: 1, minWidth: 0 }}>
            KNLS Training Programs
          </Typography>
          <Button
            color="inherit"
            component={RouterLink}
            to="/terms-and-conditions"
            startIcon={<GavelIcon />}
            sx={{
              fontWeight: 700,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.08)',
              '&:hover': { background: 'rgba(255,255,255,0.18)' },
              textDecoration: 'none',
            }}
          >
            Terms & Conditions
          </Button>
          <Button
            color="inherit"
            startIcon={<DownloadIcon />}
            onClick={() => setApkDialogOpen(true)}
            sx={{ fontWeight: 700, borderRadius: 2, background: 'rgba(255,255,255,0.08)', '&:hover': { background: 'rgba(255,255,255,0.18)' } }}
          >
            Download APK
          </Button>
        </Toolbar>
      </AppBar>

      {/* APK Disclaimer Dialog */}
      <Dialog open={apkDialogOpen} onClose={() => setApkDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#4f46e5' }}>Download KNLS App APK</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Please read and accept the disclaimer before downloading:
            </Typography>
            <ul style={{ marginLeft: 18, marginBottom: 0, color: '#222', fontSize: 15 }}>
              <li>This course/app purchase is <b>not refundable at any cost</b>.</li>
              <li>By downloading, you agree to all terms and conditions as per KNLS policy.</li>
              <li>All content is for personal use only. Redistribution is strictly prohibited.</li>
              <li>For support, contact the official KNLS team only.</li>
              <li>Downloading this APK means you accept all risks and responsibilities.</li>
              <li>Similar to Udemy, Coursera, etc., <b>no refund or exchange</b> is possible after download.</li>
            </ul>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={disclaimerAccepted}
                onChange={e => setDisclaimerAccepted(e.target.checked)}
                color="primary"
                id="disclaimer-accept"
              />
              <label htmlFor="disclaimer-accept" style={{ fontSize: 15, color: '#333', cursor: 'pointer' }}>
                I have read and accept the above disclaimer
              </label>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<DownloadIcon />}
            href={disclaimerAccepted ? APK_LINK : undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!disclaimerAccepted}
            sx={{ fontWeight: 700, borderRadius: 2, py: 1.2, fontSize: 17 }}
            onClick={() => setApkDialogOpen(false)}
          >
            {disclaimerAccepted ? 'Download Now' : 'Accept Disclaimer to Download'}
          </Button>
        </DialogContent>
      </Dialog>

      <Container maxWidth="xl" sx={{ py: 3, fontSize: `${zoomPercent}%` }}>

      {/* ── Hero header ── */}
      <Box
        sx={{
          p: { xs: 3, md: 4.5 },
          borderRadius: 4,
          color: 'white',
          background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4f46e5 80%, #7c3aed 100%)',
          boxShadow: '0 24px 48px rgba(79,70,229,0.4)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* decorative blobs */}
        <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -60, left: '40%', width: 240, height: 240, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

        <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h3'} fontWeight={900} sx={{ letterSpacing: -0.5 }}>
              KNLS- Training Programs
            </Typography>
            <Typography sx={{ mt: 1, opacity: 0.85, maxWidth: 520 }}>
              Job-ready programs with transparent fee, full syllabus, and live database — shown straight to your enquiry visitors.
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.8} alignSelf={isMobile ? 'flex-start' : 'center'} flexShrink={0}>
            <Tooltip title="Zoom out">
              <IconButton onClick={zoomOut} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Chip label={`${zoomPercent}%`} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.18)', fontWeight: 700, minWidth: 56, cursor: 'default' }} />
            <Tooltip title="Zoom in">
              <IconButton onClick={zoomIn} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset zoom">
              <IconButton onClick={resetZoom} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.5 }}>
          <Chip icon={<AutoAwesomeIcon sx={{ color: 'white !important' }} />} label={`${stats.total} Programs`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 700 }} />
          <Chip icon={<StarIcon sx={{ color: '#fbbf24 !important' }} />} label={`${stats.featuredCount} Featured`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 700 }} />
        </Stack>
      </Box>

      <Card
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.8 }}>
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Campus Glimpse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ఏ ఫోటోపై click చేసినా zoom open అవుతుంది. Left/Right తో అన్ని photos carousel లో చూడచ్చు.
              </Typography>
            </Box>
            <Chip label={`${campusImages.length} Photos`} sx={{ fontWeight: 700 }} />
          </Stack>

          <Grid container spacing={1.4}>
            {campusImages.map((image, index) => (
              <Grid item xs={6} md={3} key={image.src}>
                <Box
                  onClick={() => openCampusPreview(index)}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'zoom-in',
                    height: { xs: 130, sm: 160, md: 170 },
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 6px 18px rgba(15,23,42,0.08)',
                    '& img': {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.35s ease, filter 0.35s ease',
                    },
                    '&:hover img': {
                      transform: 'scale(1.1)',
                      filter: 'saturate(1.12)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={image.src}
                    alt={image.title}
                    loading="lazy"
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      px: 1,
                      py: 0.7,
                      background: 'linear-gradient(to top, rgba(2,6,23,0.72), rgba(2,6,23,0))',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 700 }}>
                      {image.title}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* ── Filters bar ── */}
      <Card elevation={0} sx={{ mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ py: 1.8 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search programs..."
              size="small"
              InputProps={{ startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} /> }}
              sx={{ minWidth: 220, flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
                <MenuItem value="">All Categories</MenuItem>
                {filters.categories.map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Mode</InputLabel>
              <Select value={mode} label="Mode" onChange={(e) => setMode(e.target.value)}>
                <MenuItem value="">All Modes</MenuItem>
                {filters.modes.map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Sort</InputLabel>
              <Select value={sort} label="Sort" onChange={(e) => setSort(e.target.value)}>
                <MenuItem value="display">Default</MenuItem>
                <MenuItem value="fee_low">Fee: Low → High</MenuItem>
                <MenuItem value="fee_high">Fee: High → Low</MenuItem>
                <MenuItem value="duration">Duration</MenuItem>
                <MenuItem value="rating">Rating</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant={featuredOnly ? 'contained' : 'outlined'}
              startIcon={<FilterAltIcon />}
              size="small"
              onClick={() => setFeaturedOnly((prev) => !prev)}
              sx={featuredOnly ? { background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none' } : {}}
            >
              Featured
            </Button>
            <ToggleButtonGroup value={view} exclusive onChange={(_, next) => next && setView(next)} size="small">
              <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mt: 2, borderRadius: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={fetchPrograms}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {!loading && !error && programs.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>No training programs found for selected filters.</Alert>
      )}

      {/* ── Cards grid ── */}
      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        {programs.map((program, idx) => {
          const accent = ACCENT_COLORS[idx % ACCENT_COLORS.length];
          return (
            <Grid
              item
              xs={12}
              sm={view === 'grid' ? 6 : 12}
              md={view === 'grid' ? 4 : 12}
              lg={view === 'grid' ? 3 : 12}
              key={program.id}
            >
              <ProgramCard program={program} accent={accent} idx={idx} />
            </Grid>
          );
        })}
      </Grid>

      {/* ── Detail Dialog ── */}
      <Dialog
        open={Boolean(selectedProgram)}
        onClose={() => setSelectedProgram(null)}
        fullScreen
        PaperProps={{ sx: { borderRadius: 0, overflow: 'hidden' } }}
      >
        {selectedProgram && (() => {
          const highlights = toArray(selectedProgram.highlights);
          const syllabus = toArray(selectedProgram.syllabus);
          const tags = toArray(selectedProgram.tags);
          return (
            <>
              {/* Colored dialog header */}
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${selectedAccent.dark} 0%, ${selectedAccent.mid} 100%)`,
                  px: 3,
                  pt: 3,
                  pb: 2.5,
                  color: 'white',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Chip
                      label={selectedProgram.program_code}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.22)',
                        color: 'white',
                        fontWeight: 700,
                        mb: 1,
                        transition: 'transform 0.24s ease, background-color 0.24s ease, color 0.24s ease',
                        transformOrigin: 'left center',
                        '&:hover': {
                          transform: 'scale(1.2)',
                          bgcolor: '#ffffff',
                          color: selectedAccent.dark,
                        },
                      }}
                    />
                    <Typography
                      variant="h5"
                      fontWeight={900}
                      sx={{
                        lineHeight: 1.25,
                        display: 'block',
                        transformOrigin: 'left center',
                        transition: 'transform 0.24s ease, color 0.24s ease, text-shadow 0.24s ease',
                        '&:hover': {
                          transform: 'scale(1.18)',
                          color: '#fde68a',
                          textShadow: '0 8px 22px rgba(0,0,0,0.24)',
                        },
                      }}
                    >
                      {selectedProgram.title}
                    </Typography>
                    {selectedProgram.subtitle && (
                      <Typography
                        sx={{
                          mt: 0.5,
                          opacity: 0.88,
                          display: 'block',
                          transformOrigin: 'left center',
                          transition: 'transform 0.24s ease, color 0.24s ease',
                          '&:hover': {
                            transform: 'scale(1.14)',
                            color: '#fef3c7',
                          },
                        }}
                      >
                        {selectedProgram.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <IconButton onClick={() => setSelectedProgram(null)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.18)', ml: 1, flexShrink: 0 }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>

                {/* Key meta chips */}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                  {[
                    `${selectedProgram.duration_days} Days`,
                    `₹${Number(selectedProgram.fee || 0).toLocaleString('en-IN')}`,
                    selectedProgram.delivery_mode,
                  ].map((label, idx) => (
                    <Chip
                      key={label}
                      label={label}
                      size="medium"
                      sx={{
                        bgcolor: LINE_COLORS[idx % LINE_COLORS.length].bg,
                        color: LINE_COLORS[idx % LINE_COLORS.length].text,
                        fontWeight: 700,
                        fontSize: '0.98rem',
                        height: 40,
                        '& .MuiChip-label': {
                          px: 1.8,
                        },
                        transition: 'transform 0.24s ease, background-color 0.24s ease, color 0.24s ease, box-shadow 0.24s ease',
                        transformOrigin: 'center',
                        '&:hover': {
                          transform: 'scale(1.32)',
                          bgcolor: LINE_COLORS[idx % LINE_COLORS.length].bg,
                          color: LINE_COLORS[idx % LINE_COLORS.length].text,
                          boxShadow: `0 14px 30px ${selectedAccent.dark}4d`,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <DialogContent sx={{ p: 3 }}>
                {/* Description */}
                {selectedProgram.description && (
                  <Typography variant="body1" sx={{ mb: 2.5, color: 'text.secondary', lineHeight: 1.7 }}>
                    {selectedProgram.description}
                  </Typography>
                )}

                <Grid container spacing={3}>
                  {/* Highlights */}
                  <Grid item xs={12} md={highlights.length > 0 ? 5 : 12}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <TrophyIcon sx={{ color: selectedAccent.dark }} />
                      <Typography variant="h6" fontWeight={800}>Highlights</Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                      Hover for inline emphasis. Click any line to open center spotlight.
                    </Typography>
                    {highlights.length > 0 ? (
                      <Stack spacing={1}>
                        {highlights.map((item, i) => (
                          <Stack
                            key={i}
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                            onMouseEnter={() => {
                              setActiveHighlightIndex(i);
                            }}
                            onMouseLeave={() => {
                              setActiveHighlightIndex((current) => (current === i ? null : current));
                            }}
                            onClick={() => showHighlightPreview(item, i)}
                            onContextMenu={(event) => {
                              event.preventDefault();
                              hideHighlightPreview();
                            }}
                            sx={{
                              p: 1.2,
                              borderRadius: 2,
                              bgcolor: activeHighlightIndex === i ? LINE_COLORS[i % LINE_COLORS.length].bg : `${LINE_COLORS[i % LINE_COLORS.length].bg}AA`,
                              border: '1px solid',
                              borderColor: activeHighlightIndex === i ? LINE_COLORS[i % LINE_COLORS.length].border : 'transparent',
                              transition: 'transform 0.24s ease, box-shadow 0.24s ease, background-color 0.24s ease',
                              cursor: 'pointer',
                              transform: activeHighlightIndex === i ? 'translateX(16px) scale(1.12)' : 'translateX(0) scale(1)',
                              boxShadow: activeHighlightIndex === i ? `0 18px 38px ${selectedAccent.dark}33` : 'none',
                              '&:hover': {
                                transform: 'translateX(16px) scale(1.12)',
                                boxShadow: `0 18px 38px ${selectedAccent.dark}33`,
                                bgcolor: '#fff',
                              },
                            }}
                          >
                            <CheckCircleIcon sx={{ color: selectedAccent.dark, fontSize: 18, flexShrink: 0 }} />
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                color: LINE_COLORS[i % LINE_COLORS.length].text,
                                transition: 'color 0.2s ease',
                                '&:hover': { color: selectedAccent.dark },
                              }}
                            >
                              {item}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No highlights listed.</Typography>
                    )}
                  </Grid>

                  {/* Syllabus */}
                  {syllabus.length > 0 && (
                    <Grid item xs={12} md={7}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <LibraryIcon sx={{ color: selectedAccent.dark }} />
                        <Typography variant="h6" fontWeight={800}>Syllabus</Typography>
                        <Chip label={`${syllabus.length} topics`} size="small" sx={{ bgcolor: selectedAccent.light, color: selectedAccent.text, fontWeight: 700 }} />
                      </Stack>
                      <Stack spacing={0.8}>
                        {syllabus.map((item, i) => (
                          <Stack
                            key={i}
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            onMouseEnter={() => {
                              setActiveSyllabusIndex(i);
                            }}
                            onMouseLeave={() => {
                              setActiveSyllabusIndex((current) => (current === i ? null : current));
                            }}
                            onClick={() => showSyllabusPreview(item, i)}
                            sx={{
                              position: 'relative',
                              p: 1,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: activeSyllabusIndex === i ? LINE_COLORS[i % LINE_COLORS.length].border : 'divider',
                              bgcolor: activeSyllabusIndex === i ? LINE_COLORS[i % LINE_COLORS.length].bg : 'transparent',
                              boxShadow: activeSyllabusIndex === i ? `0 22px 44px ${selectedAccent.dark}33` : 'none',
                              transform: activeSyllabusIndex === i ? 'scale(1.12)' : 'scale(1)',
                              transition: 'transform 0.24s ease, border-color 0.24s ease, background 0.24s ease, box-shadow 0.24s ease',
                              cursor: 'pointer',
                              zIndex: activeSyllabusIndex === i ? 2 : 1,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 26,
                                height: 26,
                                borderRadius: '50%',
                                bgcolor: activeSyllabusIndex === i ? LINE_COLORS[i % LINE_COLORS.length].text : LINE_COLORS[i % LINE_COLORS.length].bg,
                                color: activeSyllabusIndex === i ? '#fff' : selectedAccent.text,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: '0.7rem',
                                flexShrink: 0,
                                transform: activeSyllabusIndex === i ? 'scale(1.2)' : 'scale(1)',
                                transition: 'transform 0.24s ease, background-color 0.24s ease, color 0.24s ease',
                              }}
                            >
                              {i + 1}
                            </Box>
                            <Typography
                              variant="body2"
                              fontWeight={activeSyllabusIndex === i ? 800 : 500}
                              sx={{
                                color: activeSyllabusIndex === i ? LINE_COLORS[i % LINE_COLORS.length].text : 'text.primary',
                                transform: activeSyllabusIndex === i ? 'scale(1.1)' : 'scale(1)',
                                transformOrigin: 'left center',
                                transition: 'color 0.24s ease, font-weight 0.24s ease, transform 0.24s ease',
                              }}
                            >
                              {item}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>

                {/* Tags */}
                {tags.length > 0 && (
                  <>
                    <Divider sx={{ my: 2.5 }} />
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {tags.map((tag, i) => (
                        <Chip
                          key={i}
                          label={`#${tag}`}
                          size="small"
                          sx={{
                            bgcolor: selectedAccent.light,
                            color: selectedAccent.text,
                            fontWeight: 600,
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            cursor: 'default',
                            '&:hover': {
                              transform: 'scale(1.12)',
                              boxShadow: `0 4px 10px ${selectedAccent.dark}33`,
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </>
                )}

                {highlightPreview && (
                  <Box
                    sx={{
                      position: 'fixed',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: highlightPreview.visible ? 'rgba(15,23,42,0.18)' : 'rgba(15,23,42,0)',
                      backdropFilter: highlightPreview.visible ? 'blur(6px)' : 'blur(0px)',
                      transition: 'background-color 0.22s ease, backdrop-filter 0.22s ease',
                      zIndex: 1400,
                      pointerEvents: 'none',
                    }}
                  >
                    <Box
                      onContextMenu={(event) => {
                        event.preventDefault();
                        hideHighlightPreview();
                      }}
                      sx={{
                        width: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 72px)' },
                        maxWidth: '100vw',
                        minHeight: { xs: 220, md: 280 },
                        maxHeight: 'calc(100vh - 24px)',
                        overflowY: 'auto',
                        borderRadius: { xs: 2.5, md: 3.5 },
                        background: `linear-gradient(135deg, ${LINE_COLORS[(highlightPreview.index || 0) % LINE_COLORS.length].text} 0%, ${selectedAccent.mid} 100%)`,
                        color: '#fff',
                        boxShadow: `0 38px 90px ${selectedAccent.dark}66`,
                        px: { xs: 2.2, md: 4 },
                        py: { xs: 2.2, md: 3.6 },
                        transform: highlightPreview.visible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.88)',
                        opacity: highlightPreview.visible ? 1 : 0,
                        transition: 'transform 0.22s ease, opacity 0.22s ease',
                        pointerEvents: 'auto',
                        cursor: 'zoom-out',
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)', fontWeight: 700 }}>
                          {highlightPreview.source === 'syllabus'
                            ? `${(highlightPreview.index || 0) + 1} / ${selectedSyllabus.length || 1}`
                            : `${(highlightPreview.index || 0) + 1} / ${selectedHighlights.length || 1}`}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => navigateHighlight(-1)}
                            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                          >
                            <ArrowBackIosNewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => navigateHighlight(1)}
                            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                          >
                            <ArrowForwardIosIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={hideHighlightPreview}
                            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Stack direction="row" spacing={1.2} alignItems="flex-start">
                        <CheckCircleIcon sx={{ fontSize: { xs: 30, md: 40 }, color: '#fde68a', mt: 0.3, flexShrink: 0 }} />
                        <Box sx={{ pr: { xs: 0.5, md: 2 } }}>
                          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.72)', letterSpacing: 1.2, fontWeight: 700, fontSize: { xs: '0.68rem', md: '0.74rem' } }}>
                            {highlightPreview.source === 'syllabus' ? 'Syllabus Focus' : 'Highlight Focus'}
                          </Typography>
                          <Typography variant="h4" fontWeight={900} sx={{ lineHeight: 1.22, mt: 0.5, fontSize: 'clamp(1.5rem, 4.2vw, 4rem)' }}>
                            {highlightPreview.item}
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1.6, color: 'rgba(255,255,255,0.9)', fontSize: 'clamp(0.95rem, 2vw, 1.3rem)' }}>
                            Use ← / → keys to navigate, ESC to close, or click the X button. Hover another line to switch focus smoothly.
                          </Typography>
                          <Stack direction="row" spacing={1.2} sx={{ mt: 2.2 }}>
                            <Button
                              variant="outlined"
                              onClick={() => navigateHighlight(-1)}
                              startIcon={<ArrowBackIosNewIcon />}
                              sx={{
                                color: '#fff',
                                borderColor: 'rgba(255,255,255,0.45)',
                                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.16)' },
                              }}
                            >
                              Previous
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => navigateHighlight(1)}
                              endIcon={<ArrowForwardIosIcon />}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.22)',
                                color: '#fff',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.35)', boxShadow: 'none' },
                              }}
                            >
                              Next
                            </Button>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 2.5 }} />
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    background: `linear-gradient(135deg, ${selectedAccent.dark} 0%, ${selectedAccent.mid} 100%)`,
                    borderRadius: 2,
                    fontWeight: 800,
                    fontSize: '1rem',
                    py: 1.4,
                    letterSpacing: 0.5,
                    transition: 'transform 0.2s ease',
                    '&:hover': { transform: 'scale(1.02)' },
                  }}
                  onClick={() => setSelectedProgram(null)}
                >
                  Close
                </Button>
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      <Dialog
        open={campusPreviewOpen}
        onClose={closeCampusPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={800}>
              Campus Gallery
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`${activeCampusImageIndex + 1} / ${campusImages.length}`}
                size="small"
                sx={{ fontWeight: 700 }}
              />
              <IconButton onClick={closeCampusPreview}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#0f172a',
              minHeight: { xs: 220, md: 420 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={campusImages[activeCampusImageIndex]?.src}
              alt={campusImages[activeCampusImageIndex]?.title}
              sx={{
                maxWidth: '100%',
                maxHeight: '74vh',
                objectFit: 'contain',
                transform: 'scale(1.04)',
                transition: 'transform 0.22s ease',
              }}
            />

            <IconButton
              onClick={() => moveCampusImage(-1)}
              sx={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(15,23,42,0.55)',
                '&:hover': { bgcolor: 'rgba(15,23,42,0.8)' },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              onClick={() => moveCampusImage(1)}
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(15,23,42,0.55)',
                '&:hover': { bgcolor: 'rgba(15,23,42,0.8)' },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 1.5, overflowX: 'auto', pb: 0.5 }}>
            {campusImages.map((image, index) => (
              <Box
                key={image.src}
                onClick={() => setActiveCampusImageIndex(index)}
                sx={{
                  borderRadius: 1.6,
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: index === activeCampusImageIndex ? selectedAccent.dark : 'transparent',
                  cursor: 'pointer',
                  width: 90,
                  height: 64,
                  flexShrink: 0,
                  boxShadow: index === activeCampusImageIndex ? `0 6px 16px ${selectedAccent.dark}44` : 'none',
                }}
              >
                <Box
                  component="img"
                  src={image.src}
                  alt={image.title}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ))}
          </Stack>

        </DialogContent>
      </Dialog>

    </Container>
  </>);
}

export default TrainingPrograms;
